/**
 * Component requirements for landing-page theme
 * 
 * Declares design tokens and property data fields used by each component.
 */

import type { ComponentRequirements } from '@vibesites/tokens';
import {
  defineComponentRequirements,
  branding,
  contact,
  content,
  urls,
  details,
  collections,
} from '@vibesites/tokens';

// ============================================================================
// Component Requirements
// ============================================================================

export const headerRequirements = defineComponentRequirements('Header', {
  designTokens: [
    'color.primary',
    'color.secondary',
    'color.textOnPrimary',
    'color.textOnSecondary',
    'font.heading',
    'font.body',
    'font.accent',
    'shadow.md',
    'radius.md',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name displayed in header'),
    branding.optional('property_primary_logo', 'Primary logo (fallback)'),
    branding.optional('property_mono_white_logo', 'Mono white logo (used on dark/medium primary)'),
    branding.optional('property_mono_black_logo', 'Mono black logo (used on light primary)'),
    contact.optional('property_call_tracking_phone', 'PPC call tracking number (preferred)'),
    contact.optional('property_phone', 'Fallback phone number'),
    urls.optional('property_website_url', 'Website URL for CTA button'),
  ],
});

export const heroRequirements = defineComponentRequirements('Hero', {
  designTokens: [
    'color.primary',
    'color.textOnPrimary',
    'color.border',
    'font.heading',
    'font.body',
    'radius.lg',
    'radius.md',
    'shadow.xl',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name in hero'),
    branding.optional('property_featured_image', 'Hero background image'),
    content.optional('property_bedrooms_range', 'Bedroom range'),
    contact.optional('property_address_city', 'City name'),
    contact.optional('property_address_state', 'State abbreviation'),
    collections.optional('photo_gallery', 'Gallery for background image'),
  ],
});

export const specialOfferRequirements = defineComponentRequirements('SpecialOffers', {
  designTokens: [
    'color.primary',
    'color.textOnPrimary',
    'font.heading',
  ],
  dataTokens: [
    collections.optional('special_offers', 'Special offers collection'),
  ],
});

export const propertyOverviewRequirements = defineComponentRequirements('PropertyOverview', {
  designTokens: [
    'color.background',
    'color.text',
    'color.primary',
    'color.secondary',
    'color.accent',
    'color.surface',
    'color.textMuted',
    'font.heading',
    'font.body',
    'font.accent',
    'radius.lg',
    'shadow.md',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name'),
    content.required('property_short_description', 'Property description'),
    contact.optional('property_call_tracking_phone', 'PPC call tracking number (preferred)'),
    contact.optional('property_phone', 'Fallback phone number'),
    contact.optional('property_full_address', 'Full address'),
    collections.optional('photo_gallery', 'Gallery images'),
  ],
});

export const floorplansRequirements = defineComponentRequirements('FloorplansList', {
  designTokens: [
    'color.surface',
    'color.text',
    'color.textMuted',
    'color.primary',
    'color.textOnPrimary',
    'font.heading',
    'font.body',
    'radius.md',
    'shadow.sm',
  ],
  dataTokens: [
    collections.optional('floorplan_groups', 'Floor plan groups'),
    collections.optional('floorplans', 'Floor plans'),
  ],
});

export const amenitiesRequirements = defineComponentRequirements('AmenitiesList', {
  designTokens: [
    'color.background',
    'color.text',
    'color.secondary',
    'font.heading',
    'font.body',
  ],
  dataTokens: [
    collections.optional('amenities', 'Amenities list'),
  ],
});

export const feesRequirements = defineComponentRequirements('Fees', {
  designTokens: [
    'color.primary',
    'color.textOnPrimary',
    'font.heading',
    'font.body',
    'radius.full',
  ],
  dataTokens: [
    details.optional('property_ispetfriendly', 'Pet friendly flag'),
  ],
});

export const neighborhoodRequirements = defineComponentRequirements('NeighborhoodMap', {
  designTokens: [
    'color.background',
    'color.text',
    'color.surface',
    'color.textMuted',
    'font.heading',
    'font.body',
    'radius.lg',
    'radius.md',
    'shadow.md',
  ],
  dataTokens: [
    content.optional('property_neighborhood_blurb', 'Neighborhood description'),
    contact.optional('property_full_address', 'Full address for map'),
    contact.optional('property_address_latitude', 'Latitude'),
    contact.optional('property_address_longitude', 'Longitude'),
    collections.optional('neighborhood_pois', 'Nearby POIs'),
  ],
});

export const ctaSectionRequirements = defineComponentRequirements('CTASection', {
  designTokens: [
    'color.secondary',
    'color.primary',
    'color.textOnSecondary',
    'color.textOnPrimary',
    'font.accent',
    'radius.md',
  ],
  dataTokens: [
    contact.optional('property_call_tracking_phone', 'PPC call tracking number (preferred)'),
    contact.optional('property_phone', 'Fallback phone number'),
  ],
});

export const footerRequirements = defineComponentRequirements('Footer', {
  designTokens: [
    'color.primary',
    'color.textOnPrimary',
    'font.heading',
    'font.body',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name'),
    branding.optional('property_primary_logo', 'Primary logo (fallback)'),
    branding.optional('property_mono_white_logo', 'Mono white logo (used on dark/medium primary)'),
    branding.optional('property_mono_black_logo', 'Mono black logo (used on light primary)'),
    contact.optional('property_full_address', 'Full address'),
    contact.optional('property_call_tracking_phone', 'PPC call tracking number (preferred)'),
    contact.optional('property_phone', 'Fallback phone number'),
    contact.optional('property_email', 'Email'),
    content.optional('property_copyright', 'Copyright text'),
    urls.optional('property_facebook_url', 'Facebook'),
    urls.optional('property_instagram_url', 'Instagram'),
    urls.optional('property_twitter_url', 'Twitter'),
    urls.optional('property_linkedin_url', 'LinkedIn'),
    urls.optional('property_youtube_url', 'YouTube'),
    collections.optional('office_hours', 'Office hours'),
  ],
});

// ============================================================================
// Aggregated Requirements
// ============================================================================

export const componentRequirements: Record<string, ComponentRequirements> = {
  Header: headerRequirements,
  Hero: heroRequirements,
  SpecialOffers: specialOfferRequirements,
  PropertyOverview: propertyOverviewRequirements,
  FloorplansList: floorplansRequirements,
  AmenitiesList: amenitiesRequirements,
  Fees: feesRequirements,
  NeighborhoodMap: neighborhoodRequirements,
  CTASection: ctaSectionRequirements,
  Footer: footerRequirements,
};

/**
 * Get requirements for a specific component.
 */
export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

/**
 * Get all registered component names.
 */
export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
